/**
 * GRIDHEIMR Dashboard Layout
 *
 * Main layout wrapper for dashboard pages with navigation and header.
 */

import React, { useEffect } from "react";
import { useAuth } from "../hooks/useAuth";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { BLUE, YELLOW, DARK_BG, PIXEL, MONO, ensureFonts } from "../theme";

const NAV_LINKS = [
  { to: "/dashboard", label: "Dashboard" },
  { to: "/bots/create", label: "Create Bot" },
  { to: "/credentials", label: "Credentials" },
  { to: "/lab", label: "Hatchery Lab" },
];

/**
 * Dashboard Layout Props
 */
interface DashboardLayoutProps {
  children: React.ReactNode;
}

/**
 * Dashboard Layout Component
 *
 * Provides a consistent layout with header, navigation, and content area.
 */
export function DashboardLayout({ children }: DashboardLayoutProps) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    ensureFonts("dash-fonts");
    if (!document.getElementById("dash-css")) {
      const s = Object.assign(document.createElement("style"), { id: "dash-css" });
      s.textContent = `
        .gh-nav-link:hover { color: ${YELLOW} !important; }
        .gh-logout:hover {
          border-color: ${YELLOW} !important;
          color: ${YELLOW} !important;
          box-shadow: 0 0 10px rgba(254,204,0,0.2) !important;
        }
        .gh-nav-link-active {
          color: ${YELLOW} !important;
          border-bottom: 2px solid ${YELLOW} !important;
          text-shadow: 0 0 8px rgba(254,204,0,0.4);
        }
      `;
      document.head.appendChild(s);
    }
  }, []);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <header style={styles.header}>
        <div style={styles.headerContent}>
          <Link to="/dashboard" style={styles.logoLink}>
            <span style={styles.logoBlue}>GRID</span>
            <span style={styles.logoYellow}>HEIMR</span>
          </Link>

          <div style={styles.headerRight}>
            <span style={styles.username}>{user?.email ?? user?.username}</span>
            <button onClick={handleLogout} className="gh-logout" style={styles.logoutButton}>
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav style={styles.nav}>
        <div style={styles.navContent}>
          {NAV_LINKS.map(({ to, label }) => {
            const isActive = location.pathname === to ||
              (to === "/bots/create" && location.pathname.startsWith("/bots"));
            return (
              <Link
                key={to}
                to={to}
                className={`gh-nav-link${isActive ? " gh-nav-link-active" : ""}`}
                style={styles.navLink}>
                {label}
              </Link>
            );
          })}
        </div>
      </nav>

      {/* Main Content */}
      <main style={styles.main}>
        <div style={styles.mainContent}>{children}</div>
      </main>

      {/* Footer */}
      <footer style={styles.footer}>
        <div style={styles.footerContent}>
          <p style={styles.footerText}>
            <span style={{ color: BLUE }}>GRID</span>
            <span style={{ color: YELLOW }}>HEIMR</span>
            {" "}© {new Date().getFullYear()} ·{" "}
            <a href="https://heimlandr.io" target="_blank" rel="noopener noreferrer"
              style={{ color: "rgba(255,255,255,0.3)", textDecoration: "none" }}>
              HEIMLANDR.io
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    backgroundColor: DARK_BG,
  },
  header: {
    backgroundColor: "rgba(0,10,18,0.95)",
    borderBottom: `1px solid rgba(0,106,167,0.25)`,
    boxShadow: `0 1px 20px rgba(0,106,167,0.08)`,
    position: "sticky",
    top: 0,
    zIndex: 100,
  },
  headerContent: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "1rem 2rem",
  },
  logoLink: {
    fontFamily: PIXEL,
    fontSize: "clamp(0.8rem, 2vw, 1.1rem)",
    fontWeight: 400,
    textDecoration: "none",
    letterSpacing: "0.06em",
  },
  logoBlue: {
    color: BLUE,
    textShadow: `0 0 12px rgba(0,106,167,0.6)`,
  },
  logoYellow: {
    color: YELLOW,
    textShadow: `0 0 12px rgba(254,204,0,0.4)`,
  },
  headerRight: {
    display: "flex",
    alignItems: "center",
    gap: "1rem",
  },
  username: {
    fontFamily: MONO,
    fontSize: "0.8rem",
    color: "rgba(255,255,255,0.5)",
    letterSpacing: "0.05em",
  },
  logoutButton: {
    fontFamily: PIXEL,
    fontSize: "0.55rem",
    padding: "8px 14px",
    border: `1px solid rgba(0,106,167,0.4)`,
    background: "transparent",
    color: "rgba(255,255,255,0.6)",
    cursor: "pointer",
    letterSpacing: "0.1em",
    transition: "all 0.2s ease",
  },
  nav: {
    backgroundColor: "rgba(0,10,18,0.9)",
    borderBottom: `1px solid rgba(0,106,167,0.15)`,
  },
  navContent: {
    display: "flex",
    gap: "0.5rem",
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "0 2rem",
  },
  navLink: {
    display: "inline-block",
    padding: "0.85rem 1rem",
    fontFamily: MONO,
    fontSize: "0.75rem",
    color: "rgba(255,255,255,0.5)",
    textDecoration: "none",
    borderBottom: "2px solid transparent",
    letterSpacing: "0.06em",
    transition: "color 0.2s ease",
  },
  main: { flex: 1 },
  mainContent: {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "2rem",
  },
  footer: {
    backgroundColor: "rgba(0,10,18,0.95)",
    borderTop: `1px solid rgba(0,106,167,0.1)`,
    marginTop: "auto",
  },
  footerContent: {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "1rem 2rem",
    textAlign: "center",
  },
  footerText: {
    margin: 0,
    fontFamily: MONO,
    fontSize: "0.7rem",
    color: "rgba(255,255,255,0.3)",
    letterSpacing: "0.08em",
  },
};
